import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import Modal from '../../components/modal';
import { getCustomFulfillmentArtifact } from '../../utils/queries';

// "View the file that was sent to the fulfillment provider" for one row of the
// Custom Fulfillment History table. The API keeps no copy of what it sent, so
// the artifact is regenerated on demand from current data (GET
// /admin/reports/custom-fulfillment/:id/artifact):
//   - immediate per-order path -> the partner email's HTML body
//   - weekly export path        -> the .xlsx workbook (base64), previewed here
//                                  and downloadable as the real file
const base64ToBytes = (b64) => {
	const bin = window.atob(b64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
	return bytes;
};

const ExcelPreview = ({ artifact }) => {
	const { headers, rows, truncated } = useMemo(() => {
		try {
			const wb = XLSX.read(base64ToBytes(artifact.content_base64), { type: 'array' });
			const ws = wb.Sheets[wb.SheetNames[0]];
			const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
			const [head = [], ...body] = aoa;
			const cap = 50;
			return { headers: head, rows: body.slice(0, cap), truncated: body.length > cap };
		} catch (err) {
			return { headers: [], rows: [], truncated: false };
		}
	}, [artifact.content_base64]);

	const download = () => {
		const blob = new Blob([base64ToBytes(artifact.content_base64)], { type: artifact.content_type });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = artifact.filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);
	};

	return (
		<div className='space-y-4'>
			<div className='flex items-center justify-between'>
				<div className='text-sm text-gray-500'>
					{artifact.filename} · {artifact.row_count} row(s)
				</div>
				<button type='button' className='btn-primary' onClick={download}>
					Download .xlsx
				</button>
			</div>
			<div className='overflow-auto border border-gray-200 max-h-[55vh]'>
				<table className='text-xs whitespace-nowrap'>
					<thead className='bg-gray-50 sticky top-0'>
						<tr>
							{headers.map((h, i) => (
								<th key={i} className='py-1.5 px-2 font-medium text-gray-500 text-left border-b border-gray-200'>
									{String(h ?? '')}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((r, ri) => (
							<tr key={ri} className={ri % 2 ? 'bg-gray-50/50' : ''}>
								{headers.map((_, ci) => (
									<td key={ci} className='py-1.5 px-2 border-b border-gray-100'>
										{String(r[ci] ?? '')}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{truncated ? (
				<div className='text-xs text-gray-400'>
					Preview limited to the first 50 rows — download the file for the full export.
				</div>
			) : null}
		</div>
	);
};

const EmailPreview = ({ artifact }) => (
	<div className='space-y-4'>
		<dl className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm'>
			<dt className='text-gray-400'>To</dt>
			<dd>{artifact.to || '—'}</dd>
			<dt className='text-gray-400'>Subject</dt>
			<dd>{artifact.subject}</dd>
			<dt className='text-gray-400'>Line items</dt>
			<dd>{artifact.item_count}</dd>
		</dl>
		<div
			className='border border-gray-200 p-4 max-h-[55vh] overflow-auto text-sm'
			// Rebuilt server-side by the same renderer the send path uses.
			dangerouslySetInnerHTML={{ __html: artifact.html }}
		/>
	</div>
);

export default function CustomFulfillmentArtifactModal({ open, setOpen, row, storeId }) {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState(null);
	const [error, setError] = useState('');

	useEffect(() => {
		if (!open || !row) return;
		setLoading(true);
		setError('');
		setData(null);
		getCustomFulfillmentArtifact(row.integration_event_id, {
			storeId: storeId || undefined,
		}).then((result) => {
			if (result?.success) setData(result.data);
			else setError(result?.error?.message || 'Could not load the file for this record.');
			setLoading(false);
		});
	}, [open, row, storeId]);

	const artifact = data?.artifact;

	return (
		<Modal open={open} setOpen={setOpen} size='lg' title='File sent to fulfillment provider'>
			{loading ? (
				<div className='text-sm text-gray-400'>Regenerating…</div>
			) : error ? (
				<div className='text-sm text-red-600'>{error}</div>
			) : data ? (
				<div className='space-y-4'>
					<div className='flex flex-wrap gap-x-6 gap-y-1 text-sm'>
						<span>
							<span className='text-gray-400'>Path: </span>
							{data.path === 'weekly' ? 'Weekly export' : 'Per-order email'}
						</span>
						{data.fulfillment_type ? (
							<span>
								<span className='text-gray-400'>Type: </span>
								{data.fulfillment_type}
							</span>
						) : null}
						<span>
							<span className='text-gray-400'>Sent: </span>
							{data.sent_at ? new Date(data.sent_at).toLocaleString() : '—'}
						</span>
						<span>
							<span className='text-gray-400'>Status: </span>
							{data.status}
						</span>
					</div>

					{artifact?.type === 'excel' ? (
						<ExcelPreview artifact={artifact} />
					) : artifact?.type === 'email' ? (
						<EmailPreview artifact={artifact} />
					) : (
						<div className='text-sm text-gray-600 border border-gray-200 p-4'>
							{data.unavailable_reason || 'No file is available for this record.'}
						</div>
					)}

					{data.regenerated ? (
						<p className='text-xs text-gray-400'>
							Regenerated from current data
							{data.regenerated_at ? ` at ${new Date(data.regenerated_at).toLocaleString()}` : ''}. If
							routing or order data changed after the original send, this may differ from what the
							partner received.
						</p>
					) : null}

					<div className='flex justify-end'>
						<button type='button' className='btn-secondary' onClick={() => setOpen(false)}>
							Close
						</button>
					</div>
				</div>
			) : null}
		</Modal>
	);
}
